import {useLazyQuery, useMutation} from '@apollo/client';
import {Box, Button, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';
import React from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import CreateRecommend from '../../features/recommendations/CreateRecommend/CreateRecommend';
import EditorGrid from '../../components/EditorGrid/EditorGrid';
import {useAccountContext} from '../../contexts/AccountContext';
import {
  DeleteOvrecommend,
  DeleteOvrecommendVariables,
} from '../../schema-types/DeleteOvrecommend';
import {
  OvrecommendAssociatedReccs,
  OvrecommendAssociatedReccsVariables,
} from '../../schema-types/OvrecommendAssociatedReccs';
import {
  DELETE_OV_RECOMMENDATION,
  OV_RECOMMENDATION_ASSOCIATED_RECCS,
} from '../../features/recommendations/RecommendsGrid/ovPotQueries';
import {recommendsGridStyles} from '../../features/recommendations/RecommendsGrid/styles';
import {useReviewOVRecommends} from '../../hooks/useOVRecommends';
import {sppDAColumns} from '../../features/recommendations/RecommendsGrid/sppRecommendsColumns';
import {SaltusAny} from '../../utils/SaltusAny';
import validateUUID from '../../utils/validateUUID';
import {useRequirementsDentistOrBAU} from '../../hooks/useRequirementsDentistOrBAU';

export const LeadsRecommend: React.FC = () => {
  const {createRecommendUrl} = useRequirementsDentistOrBAU();
  const navigate = useNavigate();

  return (
    <>
      THIS SHOULD NEVER BE SEEN. PLEASE CONTACT DEV TEAM
      <Box mt={1} mb={1} padding={3} bgcolor="white">
        <Box mb={2} display="flex" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Plans:</Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              navigate({pathname: `../${createRecommendUrl}`});
            }}
          >
            New plan
          </Button>
        </Box>
        <LeadRecommendsGrid />
      </Box>
    </>
  );
};

export const LeadRecommendsGrid: React.FC = () => {
  const {data, error, loading} = useReviewOVRecommends();
  const [getAssociatedReccs, {data: associatedReccsData, error: associatedReccsError}] =
    useLazyQuery<OvrecommendAssociatedReccs, OvrecommendAssociatedReccsVariables>(
      OV_RECOMMENDATION_ASSOCIATED_RECCS
    );
  const {reviewId} = useParams();
  const classes = recommendsGridStyles();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const recommends = data?.ovrecommends?.filter((r) => r?.review?.id === reviewId) || [];
  const {createRecommendUrl} = useRequirementsDentistOrBAU();
  const [deleteRecommendation] = useMutation<
    DeleteOvrecommend,
    DeleteOvrecommendVariables
  >(DELETE_OV_RECOMMENDATION, {
    onCompleted: ({deleteOvrecommend: deleteRecommendationData}) => {
      if (!deleteRecommendationData?.ok) {
        enqueueSnackbar('Unable to delete the recommendation.', {
          variant: 'error',
          autoHideDuration: null,
        });
        return;
      }
      enqueueSnackbar('The selected review has been deleted.', {
        variant: 'success',
      });
    },
    onError: (err) => {
      enqueueSnackbar(`Unable to delete the recommendation. ${err.message}`, {
        variant: 'error',
        autoHideDuration: null,
      });
    },
    update(cache, {data: updatedData}) {
      const deleteOvrecommend = updatedData?.deleteOvrecommend;
      if (!deleteOvrecommend?.ok) {
        return;
      }

      cache.modify({
        fields: {
          ovrecommends(existingRecommendations: SaltusAny, {readField}) {
            return existingRecommendations.filter(
              (n: SaltusAny) => readField('id', n) !== deleteOvrecommend.ovrecommendId
            );
          },
        },
      });
    },
  });

  const deleteSelectedRecommendation = (ovrecommendId: string) => {
    const recc = recommends.find((x) => x.id === ovrecommendId);
    if (!recc) {
      enqueueSnackbar('Unable to delete recc');
      return;
    }
    enqueueSnackbar(`Attempting to delete recommendation "${recc?.name}"`, {
      persist: true,
    });

    getAssociatedReccs({
      variables: {
        ovrecommendId: validateUUID(ovrecommendId)!,
      },
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'network-only',
    }).then(({data: assocRecsData}) => {
      if (!assocRecsData?.ovrecommendDetails?.pots) {
        throw Error();
      }

      const reccs: {name: string; id: string}[] = [];

      for (const pot of assocRecsData.ovrecommendDetails.pots) {
        // skip bank-account destination rows, which have no pot
        if (!pot?.pot) {
          // eslint-disable-next-line no-continue
          continue;
        }
        // check if its the original recc that created the pot, if so prevent delete
        const isDummyAndCreatedByRecc =
          pot?.pot.isDummy && pot.pot.originalOvrId === ovrecommendId;
        const isShellPlanAndCreatedByRecc =
          pot?.pot?.servicingRights === 'LOA required' &&
          pot.pot.originalOvrId === ovrecommendId;

        if (isShellPlanAndCreatedByRecc || isDummyAndCreatedByRecc) {
          const dependentReccs = pot.pot.ovrecommendpots;
          if (dependentReccs.length > 1) {
            for (const assocRec of pot.pot.ovrecommendpots) {
              if (assocRec.ovrecommend) {
                if (assocRec.ovrecommend.id !== ovrecommendId) {
                  reccs.push({
                    name: assocRec.ovrecommend.name || 'Unnamed',
                    id: assocRec.ovrecommend.id,
                  });
                }
              }
            }
          }
        }
      }
      if (reccs.length) {
        // remove loading
        closeSnackbar();
        enqueueSnackbar(
          <div>
            <Typography>
              Could not delete {recc?.name ? `"${recc.name}"` : 'recommendation'} as it
              contains shell plans that are used by other recommendations. <br />
              Delete these first before deleting this recommendation:
              <br />
              {reccs.map((assocRec) => {
                return (
                  <div key={assocRec.id}>
                    <a
                      style={classes.text}
                      target="_blank"
                      href={`../${createRecommendUrl}?ovrecommendId=${assocRec.id}`}
                      rel="noreferrer"
                    >
                      {assocRec.name}
                    </a>
                  </div>
                );
              })}
            </Typography>
          </div>,
          {variant: 'error'}
        );
        return;
      }

      // remove loading
      closeSnackbar();

      // eslint-disable-next-line consistent-return
      return deleteRecommendation({
        variables: {
          ovrecommendId,
        },
      });
    });
  };

  return (
    <div
      style={{
        height: '100%',
        flexGrow: 1,
        position: 'relative',
      }}
    >
      <EditorGrid
        dataGridId="AddLeadReccs"
        sx={classes.dataGridRoot}
        onRowDoubleClick={({id}) => {
          navigate({
            pathname: `../${createRecommendUrl}`,
            search: `ovrecommendId=${id}`,
          });
        }}
        columns={sppDAColumns
          .filter((x) => x.field !== 'goal')
          .map((column) => ({...column, sortable: false}))}
        rows={recommends}
        hideFooter
        error={error}
        loading={loading}
        updateFunc={(ovrecommendId) => {
          if (!ovrecommendId) {
            return;
          }
          navigate({
            pathname: `../${createRecommendUrl}`,
            search: `ovrecommendId=${ovrecommendId}`,
          });
        }}
        deleteFunc={deleteSelectedRecommendation}
        minHeight="600px"
        createFunc={false}
        createFuncLoading={false}
        syncFunc={() => {}}
      />
    </div>
  );
};

export const LeadsRecommendBuilder: React.FC = () => {
  return <CreateRecommend isSppDA />;
};
